/**
 * SuggestionReasonChips container.
 *
 * Owns which chip's explanation is revealed, and builds the per-chip handler
 * map so the View's `map` creates no closures (`react-perf`). The category →
 * copy resolution is the pure `resolveSuggestionReasons`, which DROPS any
 * category this build has no copy for — an unsupported reason renders no chip
 * rather than a generic one, per SE-5523.
 *
 * ## Why reveal state is a PAIR, not one category (SE-6265)
 *
 * The first cut kept a single `activeCategory` that press TOGGLED and hover
 * SET. On the web surface this panel actually ships on, a mouse click is
 * always preceded by `hoverIn`, so by the time `onPress` ran the category was
 * already active and the toggle resolved to `null` — clicking a chip HID its
 * explanation. Measured on a real board: hover revealed it, click did not.
 *
 * So the two reveals are tracked separately and the rendered one is
 * `pinned ?? hovered`:
 *
 * - **hover** reveals TRANSIENTLY — `onHoverOut` clears only `hovered`.
 * - **press PINS** — the explanation then survives the pointer leaving, which
 *   is what makes it readable at all: a hover-only reveal vanishes the instant
 *   the pointer moves toward the text it is meant to reveal.
 * - **pressing the pinned chip UNPINS** it, preserving the touch-dismiss
 *   intent the original toggle was written for.
 * - unpinning clears `hovered` TOO. The pointer is still over the chip at that
 *   moment, so leaving `hovered` set would fall straight back to the hover
 *   reveal and the dismissal would look like a no-op. Nothing re-reveals until
 *   a fresh `hoverIn`, which can only fire after the pointer leaves and
 *   returns.
 *
 * Both halves live in ONE state object updated by pure functional updaters, so
 * unpinning is a single atomic write rather than one setter reaching into
 * another.
 * @module features/player-kanban/components/molecules/SuggestionReasonChips
 */

import React, { useCallback, useMemo, useState } from "react";

import type { SuggestionReasonCategory } from "@/generated/graphql";

import { resolveSuggestionReasons } from "../../../constants/suggestionReasons";

import SuggestionReasonChipsView, {
  type ReasonChipHandlers,
} from "./SuggestionReasonChipsView";

/** Props for the SuggestionReasonChips container. */
interface SuggestionReasonChipsContainerProps {
  /** Categories exactly as the backend returned them. May hold more than one. */
  readonly reasons: readonly SuggestionReasonCategory[];
}

/**
 * The two independent reveals. `pinned` outranks `hovered` when both are set.
 */
interface RevealState {
  /** Pinned by a press — survives the pointer leaving. */
  readonly pinned: SuggestionReasonCategory | null;
  /** Revealed by the pointer — lasts only while it is over the chip. */
  readonly hovered: SuggestionReasonCategory | null;
}

/** Nothing revealed. Hoisted so the initial state is a stable identity. */
const NOTHING_REVEALED: RevealState = { pinned: null, hovered: null };

/**
 * Render a candidate's reason chips.
 * @param root0 - Component props.
 * @param root0.reasons - Categories from the backend.
 */
const SuggestionReasonChipsContainer = ({
  reasons,
}: SuggestionReasonChipsContainerProps) => {
  const [reveal, setReveal] = useState<RevealState>(NOTHING_REVEALED);

  const resolved = useMemo(() => resolveSuggestionReasons(reasons), [reasons]);

  const handleHoverOut = useCallback(
    () => setReveal(current => ({ ...current, hovered: null })),
    []
  );

  /** Pre-built per-chip handlers so the View never creates inline functions. */
  const handlers = useMemo(
    () =>
      resolved.reduce(
        (acc, reason) => ({
          ...acc,
          [reason.category]: {
            // Press PINS. Pressing the already-pinned chip unpins it and drops
            // the hover reveal with it — see the suppression note in the module
            // docblock.
            onPress: () =>
              setReveal(current =>
                current.pinned === reason.category
                  ? NOTHING_REVEALED
                  : { ...current, pinned: reason.category }
              ),
            onHoverIn: () =>
              setReveal(current => ({ ...current, hovered: reason.category })),
          },
        }),
        {} as Partial<Record<SuggestionReasonCategory, ReasonChipHandlers>>
      ),
    [resolved]
  );

  const revealedCategory = reveal.pinned ?? reveal.hovered;
  const activeReason =
    resolved.find(reason => reason.category === revealedCategory) ?? null;

  return (
    <SuggestionReasonChipsView
      reasons={resolved}
      activeReason={activeReason}
      handlers={handlers}
      onHoverOut={handleHoverOut}
    />
  );
};

export default SuggestionReasonChipsContainer;
